import React, { useEffect, useState } from "react";
import PageHeader from "../layout/PageHeader";
import { fetchAiHiringOverview } from "../../utils/api";

const SOURCE_COUNTS_CACHE_TTL_MS = 60 * 1000;
const sourceCountsCache = new Map();

const AiHiringOverview = (props) => {
  const locationId = props.activeLocation?.id || props.activeLocation?._id;
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState("");
  const [trendDays, setTrendDays] = useState(30);
  const [sourceCountsLoading, setSourceCountsLoading] = useState(true);
  const [sourceCountsError, setSourceCountsError] = useState("");
  const [sourceCounts, setSourceCounts] = useState({
    indeed: 0,
    ziprecruiter: 0,
    partners: 0,
  });

  const formatHours = (hours) => {
    const value = Number(hours) || 0;
    if (!value) return "—";
    if (value < 1) {
      return `${Math.round(value * 60)} mins`;
    }
    if (value >= 24) {
      return `${(value / 24).toFixed(1)} days`;
    }
    return `${value} hrs`;
  };

  useEffect(() => {
    if (!locationId) return;
    let active = true;
    setLoading(true);
    setSourceCountsLoading(true);
    fetchAiHiringOverview(locationId, trendDays)
      .then((data) => {
        if (!active) return;
        setOverview(data);
        setError("");
      })
      .catch((err) => {
        if (!active) return;
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load AI hiring overview.",
        );
        setOverview(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    const cacheKey = String(locationId);
    const cached = sourceCountsCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
      if (active) {
        setSourceCounts(cached.value);
        setSourceCountsError("");
        setSourceCountsLoading(false);
      }
    } else {
      const token = localStorage.getItem("token") || "";
      fetch(
        `/api/ai-hiring/candidates/source-counts?locationId=${encodeURIComponent(locationId)}`,
        {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      )
        .then(async (response) => {
          const payload = await response.json().catch(() => ({}));
          if (!response.ok) {
            throw new Error(
              payload?.message || payload?.error || "Could not load source data",
            );
          }
          return payload;
        })
        .then((payload) => {
          if (!active) return;
          const next = {
            indeed: Number(payload?.indeed || 0),
            ziprecruiter: Number(payload?.ziprecruiter || 0),
            partners: Number(payload?.partners || 0),
          };
          sourceCountsCache.set(cacheKey, {
            value: next,
            expiresAt: Date.now() + SOURCE_COUNTS_CACHE_TTL_MS,
          });
          setSourceCounts(next);
          setSourceCountsError("");
        })
        .catch(() => {
          if (!active) return;
          setSourceCountsError("Could not load source data");
          setSourceCounts({
            indeed: 0,
            ziprecruiter: 0,
            partners: 0,
          });
        })
        .finally(() => {
          if (active) setSourceCountsLoading(false);
        });
    }

    return () => {
      active = false;
    };
  }, [locationId, trendDays]);

  const thisMonth = overview?.thisMonth || {
    total: 0,
    passed: 0,
    pending: 0,
    failed: 0,
  };
  const maxTrendValue = Math.max(
    1,
    ...(overview?.trend || []).map((item) => item.total || 0),
  );
  const dailyTrend = overview?.dailyTrend || [];
  const maxDaily = Math.max(1, ...dailyTrend.map((item) => item.total || 0));
  const chartWidth = 600;
  const chartHeight = 160;
  const points =
    dailyTrend.length > 1
      ? dailyTrend.map((item, idx) => {
          const x = (idx / (dailyTrend.length - 1)) * chartWidth;
          const y =
            chartHeight - (item.total / maxDaily) * (chartHeight - 20) - 10;
          return `${x},${y}`;
        })
      : [];

  const traditionalHours = overview?.traditionalHireHours || 0;
  const currentHours = overview?.avgCurrentHireHours || 0;
  const savedHours = overview?.timeSavedHours || 0;
  const totalAiHours = overview?.totalInterviewDurationSeconds
    ? Math.round((overview.totalInterviewDurationSeconds / 3600) * 10) / 10
    : 0;
  const impactMax = Math.max(1, traditionalHours, currentHours);
  const traditionalWidth = Math.round((traditionalHours / impactMax) * 100);
  const currentWidth = Math.round((currentHours / impactMax) * 100);
  const totalApplicants = Number(thisMonth.total || 0);
  const indeedCount = Number(sourceCounts.indeed || 0);
  const zipCount = Number(sourceCounts.ziprecruiter || 0);
  const directApplyCount = Math.max(0, totalApplicants - indeedCount - zipCount);
  const sourceRows = [
    {
      key: "direct",
      label: "Direct Apply",
      count: directApplyCount,
      color: "#3B82F6",
    },
    { key: "indeed", label: "Indeed", count: indeedCount, color: "#22C55E" },
    {
      key: "zip",
      label: "ZipRecruiter",
      count: zipCount,
      color: "#F59E0B",
    },
  ];

  return (
    <div className="page-shell ai-hiring-page">
      <PageHeader
        title="AI Hiring Overview"
        subtitle="Your agency AI hiring metrics for this month."
        icon={
          <span role="img" aria-label="AI">
            🤖
          </span>
        }
        {...props}
      />

      <div className="ai-hiring-content">
        {error && <div className="ai-alert ai-alert-error">{error}</div>}
        {!locationId ? (
          <div className="ai-empty">
            Select a location to view AI hiring data.
          </div>
        ) : loading ? (
          <div className="ai-empty">Loading AI hiring data...</div>
        ) : (
          <>
            <div className="ai-stat-grid">
              <div className="ai-stat-card">
                <div className="ai-stat-label">Total Applicants</div>
                <div className="ai-stat-value">{thisMonth.total}</div>
                <div className="ai-stat-foot">This month</div>
              </div>
              <div className="ai-stat-card">
                <div className="ai-stat-label">Pass Rate</div>
                <div className="ai-stat-value">{overview?.passRate || 0}%</div>
                <div className="ai-stat-foot">This month</div>
              </div>
              <div className="ai-stat-card">
                <div className="ai-stat-label">Avg Score</div>
                <div className="ai-stat-value">{overview?.avgScore || 0}</div>
                <div className="ai-stat-foot">All candidates</div>
              </div>
              <div className="ai-stat-card">
                <div className="ai-stat-label">Time Saved</div>
                <div className="ai-stat-value">{formatHours(savedHours)}</div>
                <div className="ai-stat-foot">
                  Estimated saved for {thisMonth.total} applicants
                </div>
              </div>
            </div>

            <div className="ai-card ai-source-card">
              <div className="ai-card-header ai-card-header-compact">
                <div className="ai-stat-label">Applications by Source</div>
              </div>
              {sourceCountsLoading ? (
                <div className="ai-source-list">
                  {[0, 1, 2].map((idx) => (
                    <div key={`skeleton-${idx}`} className="ai-source-row">
                      <div className="ai-source-label ai-source-skeleton" />
                      <div className="ai-source-count ai-source-skeleton" />
                      <div className="ai-source-bar ai-source-skeleton" />
                      <div className="ai-source-percent ai-source-skeleton" />
                    </div>
                  ))}
                </div>
              ) : sourceCountsError ? (
                <div className="ai-empty ai-empty-subtle">Could not load source data</div>
              ) : totalApplicants === 0 ? (
                <div className="ai-empty ai-empty-subtle">No applications yet</div>
              ) : (
                <div className="ai-source-list">
                  {sourceRows.map((row) => {
                    const percent = totalApplicants
                      ? Math.round((row.count / totalApplicants) * 100)
                      : 0;
                    return (
                      <div key={row.key} className="ai-source-row">
                        <div className="ai-source-label">{row.label}</div>
                        <div className="ai-source-count">[{row.count}]</div>
                        <div className="ai-source-bar">
                          <div
                            className="ai-source-bar-fill"
                            style={{
                              width: `${Math.max(0, Math.min(100, percent))}%`,
                              background: row.color,
                            }}
                          />
                        </div>
                        <div className="ai-source-percent">{percent}%</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="ai-card">
              <div className="ai-card-header">
                <h3 className="ai-card-title">Interview Volume Trend</h3>
                <div className="ai-toggle-group">
                  {[7, 30, 90].map((days) => (
                    <button
                      key={days}
                      type="button"
                      className={
                        days === trendDays
                          ? "ai-toggle-button active"
                          : "ai-toggle-button"
                      }
                      onClick={() => setTrendDays(days)}
                    >
                      {days}d
                    </button>
                  ))}
                </div>
              </div>
              {dailyTrend.length ? (
                <div className="ai-line-chart">
                  <svg
                    viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                    className="ai-line-chart-svg"
                    aria-hidden="true"
                  >
                    <polyline
                      fill="none"
                      stroke="#2563eb"
                      strokeWidth="3"
                      points={points.join(" ")}
                    />
                    {dailyTrend.map((item, idx) => {
                      const x =
                        (idx / (dailyTrend.length - 1 || 1)) * chartWidth;
                      const y =
                        chartHeight -
                        (item.total / maxDaily) * (chartHeight - 20) -
                        10;
                      return (
                        <circle
                          key={item.label}
                          cx={x}
                          cy={y}
                          r="4"
                          fill="#60a5fa"
                        />
                      );
                    })}
                  </svg>
                  <div className="ai-line-chart-axis">
                    <span>{dailyTrend[0]?.label}</span>
                    <span>{dailyTrend[dailyTrend.length - 1]?.label}</span>
                  </div>
                </div>
              ) : (
                <div className="ai-empty">No trend data yet.</div>
              )}
            </div>

            <div className="ai-stat-grid">
              <div className="ai-stat-card">
                <div className="ai-stat-label">Hired</div>
                <div className="ai-stat-value">{thisMonth.passed}</div>
                <div className="ai-stat-foot">This month</div>
              </div>
              <div className="ai-stat-card">
                <div className="ai-stat-label">Resume Review</div>
                <div className="ai-stat-value">{thisMonth.pending}</div>
                <div className="ai-stat-foot">This month</div>
              </div>
              <div className="ai-stat-card">
                <div className="ai-stat-label">Rejected</div>
                <div className="ai-stat-value">{thisMonth.failed}</div>
                <div className="ai-stat-foot">This month</div>
              </div>
            </div>

            <div className="ai-section-grid">
              <div className="ai-card">
                <div className="ai-card-header">
                  <h3 className="ai-card-title">Time Impact</h3>
                  <span className="ai-card-note">Hours per hire</span>
                </div>
                <div className="ai-impact-list">
                  <div className="ai-impact-row">
                    <div className="ai-impact-label">Traditional hiring</div>
                    <div className="ai-impact-bar">
                      <div
                        className="ai-impact-fill"
                        style={{ width: `${traditionalWidth}%` }}
                      />
                    </div>
                    <div className="ai-impact-value">
                      {formatHours(traditionalHours)}
                    </div>
                  </div>
                  <div className="ai-impact-row">
                    <div className="ai-impact-label">AI hiring</div>
                    <div className="ai-impact-bar ai-impact-bar--light">
                      <div
                        className="ai-impact-fill ai-impact-fill--light"
                        style={{ width: `${currentWidth}%` }}
                      />
                    </div>
                    <div className="ai-impact-value">
                      {formatHours(currentHours)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="ai-card">
                <div className="ai-card-header">
                  <h3 className="ai-card-title">AI Effort</h3>
                  <span className="ai-card-note">Automated interview time</span>
                </div>
                <div className="ai-effort-grid">
                  <div>
                    <p className="ai-stat-label">Total AI time</p>
                    <p className="ai-stat-value">{formatHours(totalAiHours)}</p>
                  </div>
                  <div>
                    <p className="ai-stat-label">Completed calls</p>
                    <p className="ai-stat-value">
                      {overview?.completedInterviewCount || 0}
                    </p>
                  </div>
                  <div>
                    <p className="ai-stat-label">Avg interview duration</p>
                    <p className="ai-stat-value">
                      {overview?.avgInterviewDurationSeconds
                        ? `${Math.round(overview.avgInterviewDurationSeconds / 60)} min`
                        : "0 min"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AiHiringOverview;
